import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, Plus, Minus, SlidersHorizontal, Check } from 'lucide-react';
import { ArtistCard } from '../components/ArtistCard';
import { fetchArtists } from '../lib/api';
import { useSettings } from '../lib/useSettings';
import {
  FILTER_CITIES,
  FILTER_OCCASIONS,
  FILTER_BUDGETS,
  FILTER_PRODUCT_KITS,
  FILTER_TIERS,
  FILTER_RATINGS,
  SORT_OPTIONS,
} from '../lib/constants';

const INITIAL = {
  q: '',
  cities: [],
  occasions: [],
  min_budget: null,
  max_budget: null,
  product_kit: '',
  tier: '',
  min_rating: null,
  sort_by: '',
};

export default function Artists() {
  const [params, setParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { settings, ready: settingsReady } = useSettings();

  // Dynamic city list from admin settings, falling back to the hardcoded NCR list.
  const cityOptions = useMemo(() => {
    const list = Array.isArray(settings.cities) && settings.cities.length > 0
      ? settings.cities
      : FILTER_CITIES.map(c => c.value);
    return list.map(name => ({ value: name, label: name }));
  }, [settings.cities]);

  const initialFromUrl = useMemo(() => ({
    ...INITIAL,
    q: params.get('q') || '',
    cities: params.get('cities')?.split(',').filter(Boolean) || [],
    occasions: params.get('occasions')?.split(',').filter(Boolean) || [],
    min_budget: params.get('min_budget') ? Number(params.get('min_budget')) : null,
    max_budget: params.get('max_budget') ? Number(params.get('max_budget')) : null,
    product_kit: params.get('product_kit') || '',
    tier: params.get('tier') || '',
    min_rating: params.get('min_rating') ? Number(params.get('min_rating')) : null,
    sort_by: params.get('sort_by') || '',
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [filters, setFilters] = useState(initialFromUrl);

  // Once we know the admin's default sort, apply it (only if user hasn't explicitly chosen one yet via URL).
  const [defaultSortApplied, setDefaultSortApplied] = useState(false);
  useEffect(() => {
    if (!settingsReady || defaultSortApplied) return;
    setDefaultSortApplied(true);
    const adminDefault = settings.default_artist_sort || '';
    const urlHadSort = !!params.get('sort_by');
    if (adminDefault && !urlHadSort && !filters.sort_by) {
      setFilters(f => ({ ...f, sort_by: adminDefault }));
    }
  }, [settingsReady, settings.default_artist_sort, defaultSortApplied, filters.sort_by, params]);

  useEffect(() => {
    setLoading(true);
    const payload = {};
    if (filters.q) payload.q = filters.q;
    if (filters.cities.length) payload.cities = filters.cities.join(',');
    if (filters.occasions.length) payload.occasions = filters.occasions.join(',');
    if (filters.min_budget != null) payload.min_budget = filters.min_budget;
    if (filters.max_budget != null) payload.max_budget = filters.max_budget;
    if (filters.product_kit) payload.product_kit = filters.product_kit;
    if (filters.tier) payload.tier = filters.tier;
    if (filters.min_rating) payload.min_rating = filters.min_rating;
    if (filters.sort_by) payload.sort_by = filters.sort_by;

    fetchArtists(payload).then(setArtists).finally(() => setLoading(false));

    const sp = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => sp.set(k, String(v)));
    setParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const toggleIn = (key, value) =>
    setFilters(f => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter(v => v !== value) : [...f[key], value],
    }));

  const setBudget = (b) => {
    if (filters.min_budget === b.min && filters.max_budget === b.max) {
      setFilters(f => ({ ...f, min_budget: null, max_budget: null }));
    } else {
      setFilters(f => ({ ...f, min_budget: b.min, max_budget: b.max }));
    }
  };

  const set = (k, v) => setFilters(f => ({ ...f, [k]: f[k] === v ? '' : v }));
  const setRating = (v) => setFilters(f => ({ ...f, min_rating: f.min_rating === v ? null : v }));
  const clear = () => setFilters(INITIAL);

  const activeFilterCount =
    (filters.q ? 1 : 0) +
    filters.cities.length +
    filters.occasions.length +
    (filters.min_budget != null || filters.max_budget != null ? 1 : 0) +
    (filters.product_kit ? 1 : 0) +
    (filters.tier ? 1 : 0) +
    (filters.min_rating ? 1 : 0);

  return (
    <div data-testid="artists-page" className="bg-white">
      {/* Banner */}
      <section className="bg-[#ffefd1] pt-32 pb-16 md:pb-20">
        <div className="container-edge">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">The Directory</div>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Discover <span className="italic text-[#a465a8]">verified</span> makeup artists.
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg text-neutral-700 leading-relaxed">
            Browse hand-picked beauty professionals across Delhi NCR. Filter by city, occasion, style and budget.
          </p>

          <div className="mt-10 flex items-center gap-3 max-w-xl bg-white border border-[#eaeaea] px-4 py-3">
            <Search className="w-4 h-4 text-neutral-400" />
            <input
              data-testid="artists-search-input"
              value={filters.q}
              onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
              placeholder="Search artist, makeup style..."
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
        </div>
      </section>

      {/* Mobile filter toggle */}
      <div className="lg:hidden container-edge pt-8 flex items-center justify-between">
        <button
          onClick={() => setMobileFiltersOpen(o => !o)}
          data-testid="mobile-filters-toggle"
          className="inline-flex items-center gap-2 border border-black px-4 py-3 text-[11px] uppercase tracking-[0.2em]"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters {activeFilterCount > 0 && <span className="bg-[#eb5381] text-white px-2 py-0.5 text-[10px]">{activeFilterCount}</span>}
        </button>
        <SortPill value={filters.sort_by} onChange={(v) => setFilters(f => ({ ...f, sort_by: v }))} />
      </div>

      {/* Filters + Results */}
      <section className="container-edge py-12 md:py-16 grid lg:grid-cols-12 gap-12">
        <aside className={`lg:col-span-3 ${mobileFiltersOpen ? 'block' : 'hidden lg:block'}`} data-testid="filters-sidebar">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" /> Refine
              {activeFilterCount > 0 && <span className="bg-[#eb5381] text-white text-[10px] px-2 py-0.5">{activeFilterCount}</span>}
            </div>
            {activeFilterCount > 0 && (
              <button data-testid="clear-filters-btn" onClick={clear} className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#eb5381] inline-flex items-center gap-1">
                Clear <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* City */}
          <FilterGroup label="Artist City">
            <CityMultiSelect
              options={cityOptions}
              selected={filters.cities}
              onToggle={(v) => toggleIn('cities', v)}
              onClear={() => setFilters(f => ({ ...f, cities: [] }))}
            />
          </FilterGroup>

          {/* Occasion */}
          <FilterGroup label="Occasion">
            <div className="space-y-2.5">
              {FILTER_OCCASIONS.map(o => (
                <label key={o} className="flex items-center gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.occasions.includes(o)}
                    onChange={() => toggleIn('occasions', o)}
                    className="accent-[#eb5381] w-4 h-4"
                    data-testid={`filter-occasion-${o.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  />
                  {o}
                </label>
              ))}
            </div>
          </FilterGroup>

          {/* Budget */}
          <FilterGroup label="Budget">
            <div className="space-y-2">
              {FILTER_BUDGETS.map(b => {
                const active = filters.min_budget === b.min && filters.max_budget === b.max;
                return (
                  <button
                    key={b.label}
                    onClick={() => setBudget(b)}
                    data-testid={`filter-budget-${b.label.toLowerCase().replace(/[\s,₹+–]+/g, '-')}`}
                    className={`block w-full text-left text-sm px-3 py-2.5 border transition-colors ${
                      active ? 'border-[#eb5381] bg-[#fff8ee] text-[#eb5381]' : 'border-neutral-200 hover:border-black'
                    }`}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>
          </FilterGroup>

          {/* More Filters */}
          <div className="border-t border-[#eaeaea] pt-6">
            <button
              onClick={() => setMoreOpen(o => !o)}
              data-testid="more-filters-toggle"
              className="w-full flex items-center justify-between text-[11px] uppercase tracking-[0.25em] hover:text-[#eb5381]"
            >
              More Filters
              {moreOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>

            {moreOpen && (
              <div className="mt-6 space-y-7" data-testid="more-filters-panel">
                <FilterGroup label="Makeup Products Used" tooltip="Product preferences help match you with artists aligned to your beauty expectations & budget.">
                  <div className="space-y-3">
                    {FILTER_PRODUCT_KITS.map(k => (
                      <label key={k.value} className="flex gap-3 text-sm cursor-pointer group">
                        <input
                          type="radio"
                          name="product_kit"
                          checked={filters.product_kit === k.value}
                          onChange={() => set('product_kit', k.value)}
                          className="accent-[#eb5381] w-4 h-4 mt-0.5 shrink-0"
                          data-testid={`filter-product-${k.value}`}
                        />
                        <div>
                          <div className="font-medium">{k.label}</div>
                          <div className="text-[10px] text-neutral-500 leading-relaxed mt-0.5">{k.note}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </FilterGroup>

                <FilterGroup label="Social Credibility">
                  <div className="space-y-2.5">
                    {FILTER_TIERS.map(t => (
                      <label key={t.value} className="flex items-center gap-3 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="tier"
                          checked={filters.tier === t.value}
                          onChange={() => set('tier', t.value)}
                          className="accent-[#eb5381] w-4 h-4"
                          data-testid={`filter-tier-${t.value}`}
                        />
                        {t.label}
                      </label>
                    ))}
                  </div>
                </FilterGroup>

                <FilterGroup label="Minimum Rating">
                  <div className="flex gap-2">
                    {FILTER_RATINGS.map(r => {
                      const active = filters.min_rating === r.value;
                      return (
                        <button
                          key={r.value}
                          onClick={() => setRating(r.value)}
                          data-testid={`filter-rating-${r.value}`}
                          className={`flex-1 text-sm py-2 border transition-colors ${
                            active ? 'border-[#eb5381] bg-[#fff8ee] text-[#eb5381]' : 'border-neutral-200 hover:border-black'
                          }`}
                        >
                          {r.label}
                        </button>
                      );
                    })}
                  </div>
                </FilterGroup>

                <FilterGroup label="Sort By">
                  <select
                    data-testid="filter-sort"
                    value={filters.sort_by}
                    onChange={e => setFilters(f => ({ ...f, sort_by: e.target.value }))}
                    className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#eb5381] outline-none px-0 py-2 text-sm"
                  >
                    {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </FilterGroup>
              </div>
            )}
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="hidden lg:flex items-baseline justify-end mb-8">
            <SortPill value={filters.sort_by} onChange={(v) => setFilters(f => ({ ...f, sort_by: v }))} />
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14">
              {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="aspect-[4/5] bg-[#f7f7f7] animate-pulse" />)}
            </div>
          ) : artists.length === 0 ? (
            <div className="border border-[#eaeaea] p-12 text-center" data-testid="artists-empty-state">
              <div className="font-serif text-3xl">No matching artists found.</div>
              <p className="mt-3 text-neutral-500">Try widening your filters or use Skip the Search to let us curate for you.</p>
              <button onClick={clear} className="mt-6 bg-[#eb5381] hover:bg-[#d64570] text-white px-8 py-3 text-[11px] uppercase tracking-[0.25em]">
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14" data-testid="artists-grid">
              {artists.map(a => <ArtistCard key={a.slug} artist={a} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const FilterGroup = ({ label, children, tooltip }) => (
  <div className="mb-7">
    <div className="flex items-center gap-2 mb-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</div>
      {tooltip && (
        <span className="relative group">
          <span className="w-3.5 h-3.5 rounded-full border border-neutral-400 text-[8px] text-neutral-500 grid place-items-center cursor-help">?</span>
          <span className="absolute left-5 top-0 z-10 hidden group-hover:block w-56 bg-black text-white text-[10px] p-3 leading-snug normal-case tracking-normal">
            {tooltip}
          </span>
        </span>
      )}
    </div>
    {children}
  </div>
);

const CityMultiSelect = ({ options, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter(o => o.label.toLowerCase().includes(term));
  }, [options, q]);

  const triggerLabel = selected.length === 0
    ? 'All cities'
    : selected.length === 1 ? selected[0] : `${selected.length} cities selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="city-multiselect-trigger"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border px-3 py-2.5 text-sm transition-colors ${
          open ? 'border-[#eb5381]' : 'border-neutral-200 hover:border-black'
        }`}
      >
        <span className={selected.length ? 'text-neutral-900' : 'text-neutral-500'}>{triggerLabel}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="city-selected-chips">
          {selected.map(c => (
            <span key={c} className="inline-flex items-center gap-1 bg-[#fff5f8] border border-[#eb5381] text-[#eb5381] text-[11px] px-2 py-1">
              {c}
              <button type="button" onClick={() => onToggle(c)} data-testid={`city-chip-remove-${c.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-[#d64570]">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={onClear} className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-[#eb5381] px-1">Clear</button>
        </div>
      )}

      {open && (
        <div data-testid="city-multiselect-panel" className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#eaeaea] shadow-lg">
          <div className="flex items-center gap-2 border-b border-[#eaeaea] px-3 py-2">
            <Search className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
            <input
              autoFocus
              data-testid="city-search-input"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search city…"
              className="flex-1 bg-transparent outline-none text-sm"
            />
            {q && <button type="button" onClick={() => setQ('')} className="text-neutral-400 hover:text-black"><X className="w-3.5 h-3.5" /></button>}
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-neutral-400 text-center">No cities found</div>
            ) : filtered.map(c => {
              const active = selected.includes(c.value);
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => onToggle(c.value)}
                  data-testid={`filter-city-${c.value.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-left transition-colors ${
                    active ? 'bg-[#fff5f8] text-[#eb5381]' : 'hover:bg-neutral-50'
                  }`}
                >
                  <span>{c.label}</span>
                  <span className={`w-4 h-4 border grid place-items-center shrink-0 ${active ? 'bg-[#eb5381] border-[#eb5381]' : 'border-neutral-300'}`}>
                    {active && <Check className="w-3 h-3 text-white" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SortPill = ({ value, onChange }) => (
  <div className="relative inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-neutral-200 px-3 py-2">
    <span className="text-neutral-500">Sort</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="sort-pill"
      className="bg-transparent outline-none text-[11px] uppercase tracking-[0.2em] pr-5 appearance-none cursor-pointer"
    >
      {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <ChevronDown className="w-3 h-3 -ml-3 pointer-events-none" />
  </div>
);
